import React, {
    useEffect,
    useState,
    useCallback,
    useRef,
    useMemo,
} from 'react';

import { Widget } from '@solublestudio/soluble-design-system';
import { formatDate } from '../../utils/date';

enum DeployStatus {
    READY = 'READY',
    BUILDING = 'BUILDING',
    QUEUED = 'QUEUED',
    CANCELED = 'CANCELED',
}

const WidgetPost = () => {
    const [statusDeploy, setStatusDeploy] = useState({
        isChecked: false,
        isLoading: false,
        lastBuildedAt: null,
        lastRequestedAt: null,
    });

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const getStatusDeploy = useCallback(async () => {
        try {
            const res = await fetch('/api/deploy/status');

            if (res.status === 404) {
                throw new Error('Not found');
            }

            const data = await res.json();

            return data;
        } catch (error) {
            console.error(error?.message);

            return null;
        }
    }, []);

    useEffect(() => {
        async function checkStatus() {
            const data = await getStatusDeploy();

            if (!data) {
                return;
            }

            const { state, createdAt, buildingAt } = data;

            if (
                state === DeployStatus.READY ||
                state === DeployStatus.CANCELED
            ) {
                setStatusDeploy((prev) => ({
                    ...prev,
                    isChecked: true,
                    isLoading: false,
                    lastRequestedAt: createdAt,
                    lastBuildedAt: buildingAt,
                }));
                return;
            }

            setStatusDeploy((prev) => ({
                ...prev,
                isChecked: true,
                isLoading: true,
                lastRequestedAt: createdAt,
            }));
        }
        checkStatus();
    }, [getStatusDeploy]);

    useEffect(() => {
        if (!statusDeploy?.isLoading) {
            intervalRef.current = null;
            return;
        }

        if (!intervalRef.current) {
            intervalRef.current = setInterval(async () => {
                const data = await getStatusDeploy();

                if (!data) {
                    return;
                }

                const { state, createdAt, buildingAt } = data;

                if (
                    state === DeployStatus.READY ||
                    state === DeployStatus.CANCELED
                ) {
                    setStatusDeploy((prev) => ({
                        ...prev,
                        isChecked: true,
                        isLoading: false,
                        lastRequestedAt: createdAt,
                        lastBuildedAt: buildingAt,
                    }));

                    setTimeout(() => {
                        window.location.reload();
                    }, 300);
                }
            }, 15 * 1000);
            return;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef?.current);
            }
        };
    }, [getStatusDeploy, statusDeploy?.isLoading]);

    const handleClick = useCallback(async () => {
        if (statusDeploy?.isLoading) {
            return;
        }

        try {
            const res = await fetch('/api/deploy/request', {
                method: 'POST',
            });

            const data = await res.json();

            if (!data?.id) {
                throw new Error('Error on deploy');
            }

            setStatusDeploy((prev) => ({
                ...prev,
                isChecked: true,
                isLoading: true,
                lastRequestedAt: data?.createdAt,
            }));
        } catch (error) {
            console.error(error?.message);
        }
    }, [statusDeploy?.isLoading]);

    const text = useMemo(() => {
        if (!statusDeploy) {
            return;
        }

        if (statusDeploy?.isLoading) {
            return (
                <ul>
                    <li>
                        <strong>Último deploy solicitado:</strong>
                        {` ${formatDate(statusDeploy?.lastRequestedAt)}`}
                    </li>
                    <li>
                        <strong>Estado actual:</strong> en construcción
                    </li>
                </ul>
            );
        }

        return (
            <ul>
                <li>
                    <strong>Último deploy solicitado:</strong>
                    {` ${formatDate(statusDeploy?.lastRequestedAt)}`}
                </li>
                <li>
                    <strong>Estado actual:</strong>
                    {` Finalizado el ${formatDate(
                        statusDeploy?.lastBuildedAt,
                    )}`}
                </li>
            </ul>
        );
    }, [statusDeploy]);

    if (!statusDeploy?.isChecked) {
        return null;
    }

    return (
        <Widget
            onClick={handleClick}
            isLoading={statusDeploy?.isLoading}
            text={text}
        />
    );
};

export default WidgetPost;
